import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Input, useDisclosure } from "@heroui/react";
import { LuPlus } from "react-icons/lu";
import { EmptyState, ErrorState, PageHeader } from "@/components/ui";
import { FilterButton, FilterCard } from "@/components/data-table";
import {
    KandangFormModal,
    BarakFormModal,
    StartBatchFormModal,
    KandangGroup,
    KandangSkeleton,
} from "./components";

import { useFetchData, useDebounce } from "@/hooks";

const Kandang = () => {
    const coopModal = useDisclosure();
    const barrackModal = useDisclosure();

    const [searchParams, setSearchParams] = useSearchParams();

    // Init state from URL params
    const [isFilterOpen, setIsFilterOpen] = useState(
        !!searchParams.get("search_kandang") ||
            !!searchParams.get("search_barak"),
    );

    // Coop Modal State
    const [modalMode, setModalMode] = useState("create");
    const [selectedCoop, setSelectedCoop] = useState(null);

    // Barrack Modal State
    const [barrackModalMode, setBarrackModalMode] = useState("create");
    const [selectedBarrack, setSelectedBarrack] = useState(null);
    const [targetCoopId, setTargetCoopId] = useState(null);

    const [searchKandang, setSearchKandang] = useState(
        searchParams.get("search_kandang") || "",
    );
    const [searchBarak, setSearchBarak] = useState(
        searchParams.get("search_barak") || "",
    );

    const debouncedKandang = useDebounce(searchKandang, 500);
    const debouncedBarak = useDebounce(searchBarak, 500);

    useEffect(() => {
        const params = {};
        if (debouncedKandang) params.search_kandang = debouncedKandang;
        if (debouncedBarak) params.search_barak = debouncedBarak;
        setSearchParams(params, { replace: true });
    }, [debouncedKandang, debouncedBarak, setSearchParams]);

    const fetchParams = {};
    if (debouncedKandang) fetchParams.search_kandang = debouncedKandang;
    if (debouncedBarak) fetchParams.search_barak = debouncedBarak;

    const fetchKandang = useFetchData("coops/data", fetchParams);

    // Start Batch Modal State
    const startBatchModal = useDisclosure();
    const [targetBarrackId, setTargetBarrackId] = useState(null);

    // Check if any filter is active
    const activeFilterCount = (searchKandang ? 1 : 0) + (searchBarak ? 1 : 0);

    // Coop Handlers
    const handleCreate = () => {
        setModalMode("create");
        setSelectedCoop(null);
        coopModal.onOpen();
    };

    const handleEdit = (coop) => {
        if (coopModal.isOpen && selectedCoop?.id === coop.id) return;
        setModalMode("edit");
        setSelectedCoop(coop);
        coopModal.onOpen();
    };

    // Barrack Handlers
    const handleCreateBarrack = (coopId) => {
        setBarrackModalMode("create");
        setSelectedBarrack(null);
        setTargetCoopId(coopId);
        barrackModal.onOpen();
    };

    const handleEditBarrack = (barrack) => {
        if (barrackModal.isOpen && selectedBarrack?.id === barrack.id) return;
        setBarrackModalMode("edit");
        setSelectedBarrack(barrack);
        setTargetCoopId(barrack.coop_id);
        barrackModal.onOpen();
    };

    const handleStartBatch = (barrack) => {
        setTargetBarrackId(barrack.id);
        startBatchModal.onOpen();
    };

    const displayCoops = fetchKandang.data || [];

    return (
        <div className="space-y-6">
            {/* Page Header - Title/Breadcrumbs left, Actions right */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Data Kandang"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Kandang" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={isFilterOpen}
                        onToggle={() => setIsFilterOpen(!isFilterOpen)}
                        activeCount={activeFilterCount}
                    />
                    <Button
                        color="primary"
                        size="sm"
                        startContent={<LuPlus />}
                        onPress={handleCreate}
                    >
                        Tambah Kandang
                    </Button>
                </div>
            </div>

            {/* Filter Panel */}
            <FilterCard
                isVisible={isFilterOpen}
                onClear={() => {
                    setSearchKandang("");
                    setSearchBarak("");
                }}
                onApply={() => setIsFilterOpen(false)}
            >
                <Input
                    label="Cari Kandang"
                    placeholder="Ketik nama kandang..."
                    value={searchKandang}
                    onValueChange={setSearchKandang}
                    variant="flat"
                    isClearable
                />
                <Input
                    label="Cari Barak"
                    placeholder="Ketik nama barak..."
                    value={searchBarak}
                    onValueChange={setSearchBarak}
                    variant="flat"
                    isClearable
                />
            </FilterCard>

            {/* Content List */}
            {fetchKandang.isLoading ? (
                <div className="space-y-8">
                    <KandangSkeleton />
                    <KandangSkeleton />
                    <KandangSkeleton />
                </div>
            ) : fetchKandang.error ? (
                <div className="py-20">
                    <ErrorState
                        title="Gagal Memuat Data"
                        description="Terjadi kesalahan saat memuat data kandang. Silakan coba lagi."
                        onAction={fetchKandang.refetch}
                        actionLabel="Coba Lagi"
                    />
                </div>
            ) : (
                <div className="space-y-8 pb-10">
                    {displayCoops.length > 0 ? (
                        displayCoops.map((kandang) => (
                            <KandangGroup
                                key={kandang.id}
                                data={kandang}
                                onEdit={() => handleEdit(kandang)}
                                onAddBarrack={() =>
                                    handleCreateBarrack(kandang.id)
                                }
                                onEditBarrack={handleEditBarrack}
                                onStartBatch={handleStartBatch}
                            />
                        ))
                    ) : (
                        <EmptyState
                            title="Data Kandang Kosong"
                            description="Belum ada data kandang untuk saat ini. Silakan buat kandang baru."
                            actionLabel="Buat Kandang Baru"
                            onAction={handleCreate}
                        />
                    )}
                </div>
            )}

            {/* Coop Modal */}
            <KandangFormModal
                isOpen={coopModal.isOpen}
                onClose={coopModal.onClose}
                mode={modalMode}
                initialData={selectedCoop}
                onSuccess={fetchKandang.refetch}
            />

            {/* Barrack Modal */}
            <BarakFormModal
                isOpen={barrackModal.isOpen}
                onClose={barrackModal.onClose}
                mode={barrackModalMode}
                initialData={selectedBarrack}
                coopId={targetCoopId}
                onSuccess={fetchKandang.refetch}
            />

            {/* Start Batch Modal */}
            <StartBatchFormModal
                isOpen={startBatchModal.isOpen}
                onClose={startBatchModal.onClose}
                barrackId={targetBarrackId}
                onSuccess={fetchKandang.refetch}
            />
        </div>
    );
};

export default Kandang;
